<template>
  <div class="flex flex-col h-80 justify-center items-center">
    <!-- bg-white rounded-lg -->
    <div
      class="w-full h-80 flex flex-col items-center justify-center relative"
      :class="{'z-20': selectedEl !== null}"
      v-if="items.length && selectedOffice"
    >
      <transition-group v-if="mode === 'tiles'" class="w-full flex flex-wrap -mx-2" name="list" tag="div">
        <div
          class="draggable-tile w-1/2"
          :data-index="index"
          :key="item.id"
          v-for="(item, index) in items"
          @mousedown="dragStart(item, $event)"
          @touchstart="dragStart(item, $event)"
          :class="{'opacity-0': selectedItem === item}"
        >
          <tile :styles="item.styles" :data="item.data"></tile>
        </div>
      </transition-group> <!-- Tiles End -->

      <tile-selector v-if="mode === 'selector'"></tile-selector>

      <div v-if="selectedEl !== null" class="delete-tile pin-r flex justify-center items-center absolute p-5 h-16 bg-white rounded-full z-30">
        <i class="fas fa-trash fa-lg mr-3"></i> Drag here to delete
      </div> <!-- Delete tile dropzone -->
    </div>

    <loader v-else></loader> <!-- Loader when items empty -->

    <div v-if="selectedEl !== null" class="blackout fixed pin z-10"></div> <!-- Blackout -->
  </div>
</template>

<script>
import Tile from '@/components/Dashboard/Tile'
import TileSelector from '@/components/Dashboard/TileSelector'
import Loader from '@/components/Loader'
import http from '@/http'
import { closestEl } from '@/helpers'
import { EventBus } from '@/EventBus'
import { mapState } from 'vuex'
import { throttle } from 'lodash'

export default {
  name: 'sortable-tiles',
  components: {
    Tile,
    Loader,
    TileSelector
  },

  data () {
    return {
      mode: 'tiles',
      items: [],
      selectedItem: null,
      selectedEl: null,
      currentPointEl: null,
      shouldDelete: false,
      canDrag: true
    }
  },

  computed: {
    ...mapState([
      'selectedOffice', 'system'
    ])
  },

  created () {
    document.querySelector('body').addEventListener('mouseup', this.dragEnd)
    document.querySelector('body').addEventListener('mousemove', this.drag)

    document.querySelector('body').addEventListener('touchend', this.dragEnd)
    document.querySelector('body').addEventListener('touchmove', this.drag)

    window.addEventListener('resize', this.isDraggingAllowed)

    EventBus.$on('tile-selector:dismiss', () => {
      this.mode = 'tiles'
      this.fetchTiles()
    })

    EventBus.$on('tile:show-selector', () => {
      this.mode = 'selector'
    })

    if (this.selectedOffice) {
      this.fetchTiles()
    }

    this.isDraggingAllowed()
  },

  beforeDestroy () {
    document.querySelector('body').removeEventListener('mouseup', this.dragEnd)
    document.querySelector('body').removeEventListener('mousemove', this.drag)

    document.querySelector('body').addEventListener('touchend', this.dragEnd)
    document.querySelector('body').addEventListener('touchmove', this.drag)

    window.removeEventListener('resize', this.isDraggingAllowed)
  },

  watch: {
    selectedOffice () { // should also be the initial trigger for loading the tiles
      this.items = []
      this.fetchTiles()
    }
  },

  methods: {
    dragStart (item, e) {
      if (!this.canDrag) {
        return
      }

      // Not a main button click
      if (!e.touches && e.button !== 0) {
        return
      }

      this.selectedItem = item
      let tile = closestEl(e.target, '.draggable-tile')
      let bounds = tile.getBoundingClientRect()

      // Create a clone
      this.selectedEl = tile.cloneNode(true)
      this.selectedEl.style.position = 'absolute'
      this.selectedEl.style.width = bounds.width + 'px'
      this.selectedEl.style.height = bounds.height + 'px'
      this.selectedEl.style.top = (bounds.top + window.scrollY) + 'px'
      this.selectedEl.style.left = (bounds.left + window.scrollX) + 'px'
      this.selectedEl.style.zIndex = '100'
      this.selectedEl.style.cursor = 'grabbing'

      document.querySelector('#app').appendChild(this.selectedEl)
    },

    dragEnd () {
      if (this.selectedEl) {
        document.querySelector('#app').removeChild(this.selectedEl)

        if (this.selectedItem && this.shouldDelete) {
          this.items.splice(this.items.indexOf(this.selectedItem), 1)
        }

        // Replace items after sort
        this.updateSortOrder(this.shouldDelete)
      }
      // Resets
      this.selectedItem = null
      this.selectedEl = null
      this.shouldDelete = false
    },

    drag (e) {
      if (this.selectedEl) {
        // Reset
        this.shouldDelete = false

        let x = e.touches ? e.touches[0].pageX : e.pageX
        let y = e.touches ? e.touches[0].pageY : e.pageY
        let cX = e.touches ? e.touches[0].clientX : e.clientX
        let cY = e.touches ? e.touches[0].clientY : e.clientY

        let bounds = this.selectedEl.getBoundingClientRect()
        let nY = (y - (bounds.height / 2))
        let nX = (x - (bounds.width / 2))

        this.selectedEl.style.top = nY + 'px'
        this.selectedEl.style.left = nX + 'px'

        /*
        elementFromPoint is based on visual elements,
        This trick is to hide the dragged element so we can determine
        the element underneath and then show the element again rapidly.
        */
        let tile = null
        this.selectedEl.hidden = true
        let els = document.elementsFromPoint(cX, cY)
        this.selectedEl.hidden = false

        // We fetch all elements as .draggable-tile could be anywhere
        // in the DOM tree, so it's nessecary to find a match.
        for (let i = 0; i < els.length; i++) {
          if (els[i].matches('.draggable-tile')) {
            tile = els[i]
            break
          }

          // Prepare for delete
          if (els[i].matches('.delete-tile')) {
            this.shouldDelete = true
          }
        }

        // We want to call shiftOrder as least times as possible,
        // so we hold a reference to the element we just shifted to
        // save on cycles.
        if (tile && this.currentPointEl !== tile) {
          this.currentPointEl = tile
          this.shiftOrder(tile.getAttribute('data-index'))
        }
      }
    },

    isDraggingAllowed:
      throttle(function () {
        this.canDrag = window.innerWidth > 576
      }, 150),

    shiftOrder (b) {
      if (this.selectedItem) {
        let a = this.items.indexOf(this.selectedItem)
        this.items.splice(a, 1, this.items.splice(b, 1, this.items[a])[0])
      }
    },

    fetchTiles () {
      if (!this.selectedOffice.id || !this.system.id) {
        return
      }

      http.get('/tiles', { params: { 'office_id': this.selectedOffice.id, 'system_id': this.system.id } }).then(res => {
        this.items = res.data
      })
    },

    updateSortOrder (replaceItems) {
      let ids = this.items
        .filter(item => item.data.id)
        .map(item => item.data.id)

      http.post('/tiles/sort', {
        tileIds: ids, 'office_id': this.selectedOffice.id, 'system_id': this.system.id
      }).then(res => {
        if (replaceItems === true) {
          this.items = res.data
        }
      })
    }
  }
}
</script>

<style lang="sass" scoped>
.blackout
  background: rgba(0, 0, 0, 0.2)

.list-move
  transition: transform .3s

.delete-tile
  margin-right: -240px
  top: 50%
  transform: translateY(-50%)

.draggable-tile
  cursor: grab
  -webkit-user-select: none
  -moz-user-select: none
  -ms-user-select: none

.list-enter, .list-leave-to
  opacity: 0
  transform: translateY(30px)

.list-leave-active
  position: absolute;
</style>
